import React from 'react';
import { Button } from '@/components/ui/button';
import { Play } from 'lucide-react';
import { PresetInfo } from '@/components/PresetCard';
import { ALL_PRESETS } from '@/config/presetConfig';
import { ambientSoundPresets } from '@/config/audioConfig';
import { trackEvent } from '@/utils/analytics';
import { EventType } from '@/types/analytics';
import { toast } from 'sonner';

interface SessionSuggestionProps {
  presetId?: string;
  customSession?: {
    frequency: number;
    duration: number;
    ambientSounds: string[];
    soundscapes: string[];
    advancedSettings?: {
      carrierFrequency: number;
      leftWaveform?: string;
      rightWaveform?: string;
    };
  };
  onStartCustomSession?: (session: {
    frequency: number;
    duration: number;
    ambientSounds: string[];
    soundscapes: string[];
    advancedSettings?: {
      carrierFrequency: number;
      leftWaveform?: string;
      rightWaveform?: string;
    };
  }) => void;
  onStartPreset?: (presetId: string) => void;
  onClose?: () => void;
}

const ChatSessionSuggestion: React.FC<SessionSuggestionProps> = ({
  presetId,
  customSession,
  onStartCustomSession,
  onStartPreset,
  onClose
}) => {
  const handleStartSession = () => {
    if (presetId) {
      onStartPreset?.(presetId);
      onClose?.();
      toast.success("Session Ready", {
        description: "Click the play button to start your session",
        duration: 5000,
      });
    } else if (customSession) {
      onStartCustomSession?.(customSession);
      onClose?.();
      toast.success("Session Ready", {
        description: "Click the play button to start your session",
        duration: 5000,
      });
    }
  };

  const preset = presetId ? ALL_PRESETS.find(p => p.id === presetId) : null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-medium">Recommended Session</h3>
        <p className="text-sm text-muted-foreground">
          {preset ? preset.description : (
            <>
              Custom session with {customSession?.frequency} Hz beat frequency
              {customSession?.advancedSettings?.carrierFrequency && `, ${customSession.advancedSettings.carrierFrequency} Hz carrier frequency`}
              {customSession?.advancedSettings?.leftWaveform && customSession.advancedSettings?.rightWaveform && 
                `, ${customSession.advancedSettings.leftWaveform}/${customSession.advancedSettings.rightWaveform} waveforms`}
              {customSession?.ambientSounds && customSession.ambientSounds.length > 0 && 
                `, with ${customSession.ambientSounds.join(', ')} ambient sounds`}
            </>
          )}
        </p>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Frequency</span>
          <span className="font-mono">{preset ? preset.frequency : customSession?.frequency} Hz</span>
        </div>
        {customSession?.advancedSettings?.carrierFrequency && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Carrier Frequency</span>
            <span className="font-mono">{customSession.advancedSettings.carrierFrequency} Hz</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Duration</span>
          <span>{preset ? preset.duration : customSession?.duration} minutes</span>
        </div>
        {customSession?.advancedSettings?.leftWaveform && customSession.advancedSettings?.rightWaveform && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Waveforms</span>
            <span className="font-mono">{customSession.advancedSettings.leftWaveform}/{customSession.advancedSettings.rightWaveform}</span>
          </div>
        )}
        {preset?.ambientSounds && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Ambient Sounds</span>
            <span>{preset.ambientSounds.map(s => s.id).join(', ')}</span>
          </div>
        )}
        {customSession?.ambientSounds && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Ambient Sounds</span>
            <span>{customSession.ambientSounds.join(', ')}</span>
          </div>
        )}
      </div>

      <Button 
        onClick={handleStartSession}
        className="w-full"
      >
        <Play className="mr-2 h-4 w-4" />
        Start Session
      </Button>
    </div>
  );
};

export default ChatSessionSuggestion; 